/**
 * Google Reviews Fetcher
 * Fetches reviews from Google Places API
 */

class GoogleReviewsFetcher {
  constructor(config) {
    this.apiKey = config.apiKey;
    this.placeId = config.placeId;
    this.cacheKey = 'rutambhara_google_reviews';
    this.cacheDuration = config.cacheDuration || 3600000; // 1 hour default
  }

  /**
   * Get reviews - from cache or API
   */
  async getReviews() {
    // Check cache first
    const cached = this.getFromCache();
    if (cached) {
      console.log('Google Reviews: Loaded from cache');
      return cached;
    }

    // Fetch from API
    try {
      const reviews = await this.fetchFromAPI();
      this.saveToCache(reviews);
      return reviews;
    } catch (error) {
      console.error('Google Reviews: Error fetching', error);
      return null;
    }
  }

  /**
   * Fetch reviews from Google Places API
   */
  async fetchFromAPI() {
    if (!this.apiKey || !this.placeId) {
      throw new Error('API Key and Place ID are required');
    }

    // Using Places API (New) - Place Details
    const url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${this.placeId}&fields=name,rating,reviews,user_ratings_total,url&key=${this.apiKey}`;

    // Note: Direct fetch from browser will have CORS issues
    // We'll use a proxy approach or JSONP alternative

    // For client-side, we need to use the Maps JavaScript API
    return new Promise((resolve, reject) => {
      if (!window.google || !window.google.maps) {
        // Load Google Maps JS API
        this.loadGoogleMapsAPI()
          .then(() => this.fetchPlaceDetails())
          .then(resolve)
          .catch(reject);
      } else {
        this.fetchPlaceDetails()
          .then(resolve)
          .catch(reject);
      }
    });
  }

  /**
   * Load Google Maps JavaScript API
   */
  loadGoogleMapsAPI() {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places&callback=googleMapsCallback`;
      script.async = true;
      script.defer = true;

      window.googleMapsCallback = () => {
        resolve();
        delete window.googleMapsCallback;
      };

      script.onerror = () => {
        reject(new Error('Failed to load Google Maps API'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Fetch place details using Google Maps Places Service
   */
  fetchPlaceDetails() {
    return new Promise((resolve, reject) => {
      // Create a dummy map element (required by PlacesService)
      const mapDiv = document.createElement('div');
      mapDiv.style.display = 'none';
      document.body.appendChild(mapDiv);

      const service = new google.maps.places.PlacesService(mapDiv);

      const request = {
        placeId: this.placeId,
        fields: ['name', 'rating', 'reviews', 'user_ratings_total', 'url', 'formatted_address', 'photos']
      };

      service.getDetails(request, (place, status) => {
        // Clean up
        document.body.removeChild(mapDiv);

        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          const data = {
            name: place.name,
            rating: place.rating,
            totalReviews: place.user_ratings_total,
            url: place.url,
            address: place.formatted_address,
            reviews: (place.reviews || []).map(review => ({
              authorName: review.author_name,
              authorPhoto: review.profile_photo_url,
              rating: review.rating,
              text: review.text,
              relativeTime: review.relative_time_description,
              time: review.time
            })),
            fetchedAt: Date.now()
          };

          console.log('Google Reviews: Fetched successfully', data);
          resolve(data);
        } else {
          reject(new Error(`Places API Error: ${status}`));
        }
      });
    });
  }

  /**
   * Get reviews from localStorage cache
   */
  getFromCache() {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (!cached) return null;

      const data = JSON.parse(cached);
      const age = Date.now() - data.fetchedAt;

      if (age > this.cacheDuration) {
        localStorage.removeItem(this.cacheKey);
        return null;
      }

      return data;
    } catch (e) {
      return null;
    }
  }

  /**
   * Save reviews to localStorage cache
   */
  saveToCache(data) {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify(data));
    } catch (e) {
      console.warn('Google Reviews: Could not cache data');
    }
  }

  /**
   * Clear cache
   */
  clearCache() {
    localStorage.removeItem(this.cacheKey);
  }

  /**
   * Generate star rating HTML
   */
  static generateStars(rating, maxStars = 5) {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = maxStars - fullStars - (hasHalfStar ? 1 : 0);

    let html = '';

    // Full stars
    for (let i = 0; i < fullStars; i++) {
      html += '<svg class="star star--full" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>';
    }

    // Half star
    if (hasHalfStar) {
      html += '<svg class="star star--half" viewBox="0 0 24 24"><defs><linearGradient id="half-grad"><stop offset="50%" stop-color="currentColor"/><stop offset="50%" stop-color="transparent"/></linearGradient></defs><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="url(#half-grad)" stroke="currentColor" stroke-width="1"/></svg>';
    }

    // Empty stars
    for (let i = 0; i < emptyStars; i++) {
      html += '<svg class="star star--empty" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>';
    }

    return html;
  }
}

// Export for use
window.GoogleReviewsFetcher = GoogleReviewsFetcher;
